import React from 'react';

type ScorePoint = {
  date: number; // epoch ms
  total: number; // 0-30
};

export function ScoreHistory({ points, height = 120 }: { points: ScorePoint[]; height?: number }) {
  const width = 480;
  const padding = 24;
  const innerWidth = width - padding * 2;
  const innerHeight = height - padding * 2;

  const sorted = [...points].sort((a, b) => a.date - b.date);
  const xs = sorted.map(p => p.date);
  const ys = sorted.map(p => p.total);
  const minX = xs.length ? Math.min(...xs) : 0;
  const maxX = xs.length ? Math.max(...xs) : 1;
  const minY = 0;
  const maxY = 30;

  const xScale = (x: number) => {
    if (maxX === minX) return padding + innerWidth / 2;
    return padding + ((x - minX) / (maxX - minX)) * innerWidth;
  };
  const yScale = (y: number) => padding + innerHeight - ((y - minY) / (maxY - minY)) * innerHeight;

  const pathD = sorted
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.date).toFixed(2)} ${yScale(p.total).toFixed(2)}`)
    .join(' ');

  return (
    <div className="border border-evat-neon/30 rounded-lg p-4 bg-[#0b0b0b]">
      <div className="text-sm text-neutral-300 mb-2">Score History</div>
      {sorted.length === 0 ? (
        <div className="text-xs text-neutral-500">No history</div>
      ) : (
        <svg width={width} height={height} className="block">
          {/* Axes */}
          <line x1={padding} y1={padding + innerHeight} x2={padding + innerWidth} y2={padding + innerHeight} stroke="#333" />
          <line x1={padding} y1={padding} x2={padding} y2={padding + innerHeight} stroke="#333" />
          {/* Gridlines (10, 20, 30) */}
          {[10, 20, 30].map(v => (
            <g key={v}>
              <line x1={padding} y1={yScale(v)} x2={padding + innerWidth} y2={yScale(v)} stroke="#222" />
              <text x={padding - 6} y={yScale(v)} textAnchor="end" dominantBaseline="middle" fill="#888" fontSize={10}>{v}</text>
            </g>
          ))}
          {/* Line */}
          <path d={pathD} fill="none" stroke="#00ff9c" strokeWidth={2} />
          {/* Dots */}
          {sorted.map((p, i) => (
            <circle key={i} cx={xScale(p.date)} cy={yScale(p.total)} r={3} fill="#00ff9c" />
          ))}
          {/* X labels (first/last) */}
          {sorted.length > 0 && (
            <>
              <text x={xScale(sorted[0].date)} y={padding + innerHeight + 14} textAnchor="start" fill="#888" fontSize={10}>
                {new Date(sorted[0].date).toLocaleDateString()}
              </text>
              <text x={xScale(sorted[sorted.length - 1].date)} y={padding + innerHeight + 14} textAnchor="end" fill="#888" fontSize={10}>
                {new Date(sorted[sorted.length - 1].date).toLocaleDateString()}
              </text>
            </>
          )}
        </svg>
      )}
    </div>
  );
}

export default ScoreHistory;



